import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useParams } from "react-router";
import { DATE_FORMAT, getDateFnsLocale } from "../date-utils";
import { FrameWithSearchBox } from "../common/Frame";
import { Alert, Grid, Paper, TableContainer } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { addDays, addMonths, eachDayOfInterval, format, formatISO, isValid, parse, startOfDay, subMonths } from "date-fns";
import { useMemo, useRef, useState } from "react";
import { DateRange } from "../common/DateRange";
import useAxios from "axios-hooks";
import { Table, TableBody, TableCell, TableHead, TableRow, useAuthenticator } from "@aws-amplify/ui-react";
import { MaterialArea } from "../schemas";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { ChartContainer } from "./Container";
import qs from "qs";
import { Column, useTable } from "react-table";

const locale = getDateFnsLocale();

interface AggregatedDataEntry {
  sceneId: string;
  areaId: string;
  sceneName: string;
  areaName: string;
  okCount: number;
  totalCount?: number;
  ngCount: number;
  showCount: number;
  waitCount: number;
}

export function ChartMaterial() {

  const { materialId } = useParams();

  const [searchParam, setSearchParams] = useSearchParams();

  const paramFrom = parse(searchParam.get('from') || '', DATE_FORMAT, new Date());
  const paramTo = parse(searchParam.get('to') || '', DATE_FORMAT, new Date());

  const today = startOfDay(new Date());
  const initFrom = isValid(paramFrom) ? paramFrom : subMonths(addDays(today, 1), 1);
  const initTo = isValid(paramTo) ? paramTo : today;

  const [from, setFrom] = useState<Date | null>(initFrom);
  const [to, setTo] = useState<Date | null>(initTo);

  const [appliedFrom, setAppliedFrom] = useState<Date | null>(initFrom);
  const [appliedTo, setAppliedTo] = useState<Date | null>(initTo);

  const stringToColor = (str: String) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
}
  
  const onSearchClicked = () => {
    if (!isValid(from) || !isValid(to)) return;
    setAppliedFrom(from);
    setAppliedTo(to);
    setSearchParams(qs.stringify({
      from: from ? format(from, DATE_FORMAT) : undefined,
      to: to ? format(to, DATE_FORMAT) : undefined,
    }),
      { replace: true }
    )
   }
  const onResetClicked = () => {
    setSearchParams("", { replace: true })
    setFrom(subMonths(addDays(today, 1), 1));
    setTo(today);
    setAppliedFrom(subMonths(addDays(today, 1), 1));
    setAppliedTo(today);
   }

  const is_vaild_date_range = (from: Date, to: Date) => from <= to && addMonths(from, 2) >= to;

  const valid_date_range = is_vaild_date_range(initFrom, initTo);

  const { user } = useAuthenticator();
  const token = user?.getSignInUserSession()?.getIdToken().getJwtToken();

  const url = `${process.env.REACT_APP_AWS_API_GATEWAY_URL}/material/${materialId}`;

  const [{ data, loading, error }] = useAxios<{ material_name: string, materials: MaterialArea[] }>(
    {
    url,
    headers: { Authorization: token },
    params: {
      from: formatISO(initFrom),
      to: formatISO(addDays(initTo, 1)),
    }
  });

  const getDatesBetween = (from: Date, to: Date) => {
    const dates = [];
    let currentDate = new Date(from);
    to = new Date(to);
    while (currentDate <= to) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  const graphData = useMemo(() => {
    const countsPerDate: { [key: string]: number } = {};

    const fromDate = new Date(appliedFrom || 0);
    const toDate = new Date(appliedTo || 0);

    const adjustedToDate = new Date(toDate);
    adjustedToDate.setDate(adjustedToDate.getDate() + 1);

    data?.materials.forEach(({ start_time }) => {
        const dateStr = start_time.split('T')[0];  // "2023-09-04T18:37:09" -> "2023-09-04"
        const date = new Date(dateStr);

        // Check if the date is within the range [from, to]
        if (date >= fromDate && date <= adjustedToDate) {
            countsPerDate[dateStr] = (countsPerDate[dateStr] || 0) + 1;
        }
    });
    
    if (from && to && isValid(from) && isValid(to)) {
      const allDatesBetween = eachDayOfInterval({ start: new Date(from), end: new Date(to) });
      allDatesBetween.forEach(date => {
        const dateString = formatISO(date, { representation: 'date' });
        countsPerDate[dateString] = countsPerDate[dateString] || 0;
      });
    }
    
    return Object.entries(countsPerDate)
      .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
      .map(([date, count]) => ({ date, count: Number(count) }));
  }, [data?.materials, appliedFrom, appliedTo]);
  
    
  const dates = getDatesBetween(initFrom, initTo);

  const ref = useRef(null);
  const nDates = dates.length;

  /**
   * Following related okGraph and table
   */
    
  const aggregatedData: AggregatedDataEntry[] = [];
    
  (data?.materials || []).forEach(material => 
    material.scenes?.forEach(scene => 
      scene.areas?.forEach(area => {
        if (area.scoring_subject === 0) {
          // whether or not same sceneName and areaName exist, true or false.
          const existingData = aggregatedData.find(d => d.sceneId === scene.id && d.areaId === area.id);
          if (!existingData) {
            // if not exist data, insert data
            aggregatedData.push({
              sceneId: scene.id,
              areaId: area.id,
              sceneName: scene.name,
              areaName: area.name,
              okCount: area.result === 'OK' ? 1 : 0,
              ngCount: area.result === 'NG' ? 1 : 0,
              showCount: area.result === 'Showing' ? 1 : 0,
              waitCount: area.result === 'Waiting' ? 1 : 0,
            });
          } else {
            // if exist data, update count
            if (area.result === 'OK') {
              existingData.okCount += 1;
            } else if (area.result === 'NG') {
              existingData.ngCount += 1;
            } else if (area.result === 'Showing') {
              existingData.showCount += 1;
            } else if (area.result === 'Waiting') {
              existingData.waitCount += 1;
            }
          }
        }
      })
    )
  );

  const tableData = aggregatedData.map(data => ({
    ...data,
    totalCount: data.okCount + data.ngCount + data.showCount + data.waitCount,
    okRate: data.totalCount !== 0 || data.okCount !== 0 ? Math.round((data.okCount / (data.okCount + data.ngCount + data.showCount + data.waitCount)) * 100) + '%' : '0%'
  }));

  let maxLength = 0;
  tableData.forEach(data => {
    const sceneNameLength = data.sceneName.length;
    const areaNameLength = data.areaName.length;
    maxLength = Math.max(maxLength, sceneNameLength, areaNameLength);
  });
  
  const columns = useMemo<Column[]>(
    () => [
      { Header: 'Scene Name', accessor: 'sceneName' },
      { Header: 'Area Name', accessor: 'areaName' },
      { Header: 'OK Rate', accessor: 'okRate' },
      { Header: 'Number of Responses', accessor: 'totalCount' },
      { Header: 'OK', accessor: 'okCount' },
      { Header: 'NG', accessor: 'ngCount' },
      { Header: 'Not Selected', accessor: 'showCount' },
      { Header: 'Not Displayed', accessor: 'waitCount' },
    ],
    []
  );

  const parseData = tableData.map(entry => ({
    Name: `${entry.sceneName.replace(/\s+/g, '')} ${entry.areaName.replace(/\s+/g, '')}`,
    okRate: parseFloat(entry.okRate),
  }));

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable({ columns, data: tableData });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <FrameWithSearchBox
        title={`Chart: ${data?.material_name || ''}`}
        onSearchClicked={onSearchClicked}
        onResetClicked={onResetClicked}
      >
        <Grid container alignItems="center" columnSpacing={2} rowSpacing={1}>
          <DateRange from={from} setFrom={setFrom} to={to} setTo={setTo} required />
          {
            !valid_date_range &&
            <Grid item xs={12}>
              <Alert severity="error">
                Invalid date range:
                'From' date must be before or equal to 'To' date,
                and the date range must be within 2 months.
              </Alert>
            </Grid>
          }
        </Grid >
        <Grid container spacing={1} sx={{ height: '100%' }}>
          <Grid item xs={12}>
            <ChartContainer title="By Date"
              content={
                loading ? (
                  <p>Loading...</p>
                ) : error ? (
                  <p>Error loading data: </p>
                ) : (
                  is_vaild_date_range(new Date(appliedFrom || 0), new Date(appliedTo || 0)) ? (
                    <ResponsiveContainer width={"100%"} height={300} >
                      <LineChart data={graphData}>
                        <Line name={data?.material_name} type="linear" dataKey="count" stroke={stringToColor(materialId || "")} strokeWidth={2} />
                        <CartesianGrid stroke="#ccc" />
                        <XAxis
                          dataKey="date"
                          padding={{ left: 30, right: 30 }}
                          interval={0}
                          tickFormatter={(date, i) => {
                            const d = new Date(date);
                            const chartWidth = (ref.current as any)?.props?.width;
                            const a = chartWidth / nDates;
                            const day = d.getDate();

                            if (a < 25) {
                              if (day % 5 !== 0) return '';
                              return day <= 5 || i <= 5 ? format(d, 'M/d') : format(d, 'd')
                            }
                            if (a < 45) {
                              if (day % 2 !== 0) return '';
                              return day <= 2 || i <= 1 ? format(d, 'M/d') : format(d, 'd')
                            }
                            return day === 1 || i === 0 ? format(d, 'M/d') : format(d, 'd')
                          }}
                        />
                        <YAxis />
                        <Tooltip />
                        <Legend verticalAlign="bottom" height={30} iconSize={20} iconType="plainline" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Grid container alignItems="center" justifyContent="center">
                    </Grid>
                  )
                )
              }
            />
          </Grid>
          <Grid item xs={12}>
            <ChartContainer title="OK Rate"
              content={
                loading ? (
                  <p>Loading...</p>
                ) : error ? (
                  <p>Error loading data: </p>
                ) : (
                  is_vaild_date_range(new Date(appliedFrom || 0), new Date(appliedTo || 0)) ? (
                    <ResponsiveContainer minHeight={parseData.length * 50 + 70}>
                      <BarChart
                        data={parseData}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: maxLength * 8 + 30, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} />
                        <YAxis
                          dataKey="Name"
                          type="category"
                          tick={{
                            fontSize: 14,
                          }}
                        />
                        <Tooltip />
                        <Legend />
                        <Bar name="OK Rate(%)" dataKey="okRate" fill="#82ca9d" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Grid container alignItems="center" justifyContent="center">
                    </Grid>
                  )
                )
              }  
              />
            </Grid>
            <Grid item xs={12}>
              <TableContainer component={Paper} {...getTableProps()}>
                <Table margin={10} border-style={"none"} width={"100%"}>
                  <TableHead>
                    {headerGroups.map(headerGroup => (
                      <TableRow {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                          <TableCell {...column.getHeaderProps()} style={{ borderBottom: 'solid 1px gray', color: 'black', fontWeight: 'bold', fontStyle: 'italic' }}>
                            {column.render('Header')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody {...getTableBodyProps()}>
                    {rows.map(row => {
                      prepareRow(row);
                      return (
                        <TableRow {...row.getRowProps()}>
                          {row.cells.map(cell => {
                            return (
                              <TableCell {...cell.getCellProps()} style={{ padding: '10px', borderBottom: 'solid 1px gray', fontStyle: 'italic' }}>
                                {cell.render('Cell')}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
        </Grid>
      </FrameWithSearchBox >
    </LocalizationProvider >
  )
}